import { gql, useApolloClient } from '@apollo/client';
import { useCallback } from 'react';

import type { ExperimentAllocation } from '@growtherapy/analytics/client';

import { Server } from '../../apollo/types';

const EXPERIMENT_ALLOCATION_QUERY = gql`
  query ExperimentAllocation($experimentKey: String!) {
    experimentAllocation(experimentKey: $experimentKey) {
      allocationValue
      allocatedAt
    }
  }
`;

type ExperimentAllocationQueryData = {
  experimentAllocation?: {
    allocationValue: string;
    allocatedAt: string;
  } | null;
};

/**
 * Read-only companion to `useRecordExperimentAllocation` for
 * `useDelayedExperiment`. Reads back the provider's allocation anchor without
 * creating one, so the cutover can still be resolved once a decision is shipped
 * on Statsig and the user is no longer reported as allocated.
 */
export function useGetExperimentAllocation(): (input: {
  experimentKey: string;
}) => Promise<ExperimentAllocation | null> {
  const client = useApolloClient();

  return useCallback(
    async ({ experimentKey }) => {
      const { data } = await client.query<ExperimentAllocationQueryData>({
        query: EXPERIMENT_ALLOCATION_QUERY,
        variables: { experimentKey },
        context: { server: Server.MONOLITH },
      });

      return data?.experimentAllocation ?? null;
    },
    [client],
  );
}
